import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';

// Zendesk article links look like https://denthub.zendesk.com/hc/sv/articles/17898754460572[-slug].
const HELP_CENTER_ARTICLE = /\/hc\/[^/]+\/articles\/(\d+)/;
// What the backend rewrites a file attachment link to (DD-3785). Keep in sync with
// backend/src/modules/zendesk/services/zendesk-attachment-path.ts.
const HELP_ARTICLE_ATTACHMENT = /^\/help-articles\/\d+\/attachments\/\d+$/;

export type HelpLink =
    | { kind: 'article'; articleId: string }
    | { kind: 'attachment'; path: string }
    | { kind: 'external' };

/**
 * DD-3785: only a help-center article link is opened inside the drawer. Before this,
 * every link was treated as an article and its last path segment sent to Zendesk as an
 * article id, which for a ~1 MB inlined PDF meant a 414 and a crashed app.
 */
export const classifyHelpLink = (href: string): HelpLink => {
    let url: URL;
    try {
        url = new URL(href, window.location.origin);
    } catch {
        return { kind: 'external' };
    }
    const article = HELP_CENTER_ARTICLE.exec(url.pathname);
    if (article) {
        return { kind: 'article', articleId: article[1] };
    }
    if (HELP_ARTICLE_ATTACHMENT.test(url.pathname)) {
        return { kind: 'attachment', path: url.pathname };
    }
    return { kind: 'external' };
};

/**
 * The help center is restricted, so the browser cannot open the Zendesk file itself; the
 * backend serves it for the logged-in user. The tab is opened before the download so the
 * popup blocker sees a user gesture, then pointed at the file once it has arrived.
 */
export const openHelpArticleAttachment = async (path: string): Promise<void> => {
    const tab = window.open('', '_blank');
    try {
        const response = await fetch(`${CONSTANTS.REST_API_URI}${path}`, {
            headers: { authorization: Cookies.get(CONSTANTS.TOKEN_KEY) || '' },
        });
        if (!response.ok) {
            throw new Error(`Help attachment ${path} failed with status ${response.status}`);
        }
        const fileUrl = URL.createObjectURL(await response.blob());
        if (tab) {
            tab.location.href = fileUrl;
        } else {
            window.open(fileUrl, '_blank');
        }
    } catch (error) {
        tab?.close();
        throw error;
    }
};
